import type { ModuleSnapshot } from '@ctcells/contract'

import { ARM_HOME } from './monitoringModel.ts'
import { robotGrips, robotRun } from './robotTransferModel.ts'

/**
 * **6축 팔 그림의 움직임 — 서버가 준 것으로만 고른다**(eric 2026-10-07 「6축 로봇 왜 전혀 안움직여?」 · 「움직임을 줘...」).
 *
 * 관절값은 서버가 안 준다(BR-114). 대신 `module_status` 가 1Hz 로 주는 세 값으로 «지금 무엇을 하는가»를 고르고, 단계마다 **도식 자세**를
 * 붙인다 — 실제 관절 자세가 아니라 「이 동작 중」을 그리는 그림이다(그림 아래 글자가 그 사실을 말한다).
 *
 * | 서버 값(dev2 2026-10-07 06:07~06:09 이송 한 번 실측 · 1Hz 프레임 628건) | 단계 |
 * | --- | --- |
 * | `current_execution.sequence` = `move_to_module` · `move_aux_axis` · `move_to_pose` · 그리퍼 `at_target` | `move` — 빈손으로 이동 |
 * | 같은 이동 · 그리퍼 `holding` | `carry` — 트레이를 쥐고 이동(반출 → 인계 사이) |
 * | `grip` · `gripper_set`(쥐는 쪽) | `grip` — 트레이 집기(반출) |
 * | `release` · `place_held` | `release` — 트레이 놓기(인계 · 회수) |
 * | `home` | `home` — 파킹 복귀 |
 * | 실행 없음 · 상태 `MOVING`/`HANDOFF` | 쥐었으면 `carry`, 아니면 `move`(실행 사이의 짧은 틈) |
 * | 그 밖(`IDLE` …) · 끊긴 화면(`stale`) | `idle` — 기준 자세 · 움직이지 않는다 |
 *
 * 🔴 다음 단계를 짐작해 미리 움직이지 않는다 · 시간을 재지 않는다 — 프레임이 바뀌면 그때 자세가 바뀌고(CSS transition), 이동 단계에서만
 *    좌우로 흔들린다. 모르는 시퀀스 이름은 `move` 로 접지 않고 **상태**(`MOVING`/`HANDOFF`)로만 간다.
 */
export type RobotPhase = 'idle' | 'move' | 'carry' | 'grip' | 'release' | 'home'

const MOVE_SEQUENCES: ReadonlySet<string> = new Set([
  'move_to_module',
  'move_aux_axis',
  'move_to_pose',
])
const GRIP_SEQUENCES: ReadonlySet<string> = new Set(['grip', 'gripper_set'])
const RELEASE_SEQUENCES: ReadonlySet<string> = new Set(['release', 'place_held'])
const BUSY_STATES: ReadonlySet<string> = new Set(['MOVING', 'HANDOFF'])

export function robotPhase(snapshot: ModuleSnapshot | undefined, stale: boolean): RobotPhase {
  if (snapshot === undefined || stale || snapshot.stale === true) return 'idle'
  const holding = (robotGrips(snapshot) ?? []).some((grip) => grip.state === 'holding')
  const sequence = robotRun(snapshot)?.sequence
  if (sequence !== undefined) {
    if (GRIP_SEQUENCES.has(sequence)) return 'grip'
    if (RELEASE_SEQUENCES.has(sequence)) return 'release'
    if (sequence === 'home') return 'home'
    if (MOVE_SEQUENCES.has(sequence)) return holding ? 'carry' : 'move'
  }
  if (BUSY_STATES.has(snapshot.state)) return holding ? 'carry' : 'move'
  return 'idle'
}

/** 이동 단계 — 팔이 좌우로 흔들린다(`ArmFigure` `moving`) */
export function robotMoving(phase: RobotPhase): boolean {
  return phase === 'move' || phase === 'carry'
}

/**
 * 단계별 **도식 자세**(J1~J6 · `ArmFigure` 는 J2 · J3 · J4 만 그린다). 실제 값이 아니라 「이 동작」을 읽히게 하는 모양 —
 * 기준 자세는 `ARM_HOME` 그대로다(팀장 2026-10-06 결정과 같은 좌표). 관절값이 오면 이 표는 쓰지 않는다.
 */
export const ROBOT_POSES: Readonly<Record<RobotPhase, readonly number[]>> = {
  idle: ARM_HOME,
  home: ARM_HOME,
  /* 빈손 이동 — 팔을 앞으로 기울인 채 */
  move: [0, -70, 45, -65, -90, 0],
  /* 쥐고 이동 — 팔을 조금 더 세워 트레이를 든 채 · 손목은 수평(절대 각 0°)이라 트레이가 눕는다(eric 2026-10-08 「트레이는 수평으로」) */
  carry: [0, -80, 60, -160, -90, 0],
  /* 집기 · 놓기 — 어깨를 앞으로, 팔꿈치를 꺾고 손목은 수평(절대 각 0°) — 포크가 옆으로 들어가 트레이를 수평으로 넣고 뺀다(웨이퍼 핸들러) */
  grip: [0, -35, 95, 120, -90, 0],
  release: [0, -35, 95, 120, -90, 0],
}
